interface ExtensionData {
    calllist: {
        number: string
        numbercalls: {
            members: {
                inbound?: {
                    channelid?: string
                    from?: string
                },
            }[]
        }[]
    }[]
}

export function usePbx() {
    const api = <T>(endpoint: string, options: {}) => $fetch<T>(endpoint, {
        baseURL: useRuntimeConfig().public.pbxUrl,
        credentials: 'omit',
        timeout: 10000,
        ...options
    })

    async function login(): Promise<string | undefined> {
        console.debug('[DEBUG] usePbx.ts login() - Starting PBX login')
        const { pbxConfig } = await $fetch('/api/calls/config')
        console.debug('[DEBUG] usePbx.ts login() - PBX config retrieved', {
            hasUsername: !!pbxConfig.username,
            hasPassword: !!pbxConfig.password
        })

        if (pbxConfig.username && pbxConfig.password) {
            console.debug('[DEBUG] usePbx.ts login() - Sending login request to PBX')
            const { token } = await api<{ token: string }>('login', {
                method: 'POST',
                body: {
                    username: pbxConfig.username,
                    password: pbxConfig.password,
                    version: '2.0.0'
                },
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
            })

            console.debug('[DEBUG] usePbx.ts login() - PBX login response', {
                hasToken: !!token,
                tokenLength: token?.length
            })
            return token
        } else {
            console.debug('[DEBUG] usePbx.ts login() - Missing PBX credentials, cannot login')
        }
    }

    async function heartbeat(token: string): Promise<void> {
        console.debug('[DEBUG] usePbx.ts heartbeat() - Sending PBX heartbeat', { tokenLength: token?.length })
        try {
            await api(`heartbeat?token=${token}`, {
                method: 'POST'
            })
            console.debug('[DEBUG] usePbx.ts heartbeat() - PBX heartbeat successful')
        } catch (error) {
            console.error('[DEBUG] usePbx.ts heartbeat() - PBX heartbeat failed', error)
            throw error
        }
    }

    async function transfer(token: string, channelId: string, extension: string): Promise<void> {
        await api(`call/transfer?token=${token}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: {
                channelid: channelId,
                number: extension
            }
        })
    }

    async function queryExtension(token: string, extension: string): Promise<{ channelId?: string, from?: string } | undefined> {
        console.debug('[DEBUG] usePbx.ts queryExtension() - Querying PBX extension', {
            extension,
            tokenLength: token?.length
        })

        try {
            const data = await api<ExtensionData>(`extension/query_call?token=${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: JSON.stringify({
                    number: extension
                })
            })

            console.debug('[DEBUG] usePbx.ts queryExtension() - PBX query response', {
                hasData: !!data,
                hasCalllist: !!data?.calllist,
                calllistLength: data?.calllist?.length,
                firstCallNumber: data?.calllist?.[0]?.number
            })

            if (data?.calllist && data.calllist[0]?.number == extension) {
                const result = {
                    channelId: data.calllist[0].numbercalls[0]?.members[0]?.inbound?.channelid,
                    from: data.calllist[0].numbercalls[0]?.members[0]?.inbound?.from
                }
                console.debug('[DEBUG] usePbx.ts queryExtension() - Extension query result', result)
                return result
            } else {
                console.debug('[DEBUG] usePbx.ts queryExtension() - No matching extension found in calllist')
            }
        } catch (error) {
            console.error('[DEBUG] usePbx.ts queryExtension() - PBX query failed', error)
            throw error
        }
    }

    return {
        login,
        heartbeat,
        transfer,
        queryExtension,
    }
}